/**
 * Markdown document editor shared by the memory panel and the trainer console. Memory documents
 * carry an integer `version`; trainer library files carry a Git blob `revision`. The caller decides
 * which concurrency token to send with each save; a 409 conflict keeps the user's draft and offers
 * to reload the latest server copy instead of overwriting it. When a history loader is supplied,
 * past commits can be viewed and restored, which saves the old content as a new commit.
 *
 * @module apps/web/src/components/flux-agent/FluxAgentDocumentEditor
 */

import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { History, Loader2, RotateCcw, Save, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  isVersionConflict,
  type FluxAgentDocument,
  type FluxAgentResult,
  type LibraryDocument,
  type LibraryRevision,
  shortId,
} from '@/lib/fluxAgentApi';
import { MAX_DOCUMENT_CHARS } from '@/lib/fluxAgentFiles';
import { cn } from '@/lib/utils';
import { parseServerTime } from '@/lib/serverTime';

/** Either a versioned memory document or a Git-backed trainer library file. */
export type EditableDocument = FluxAgentDocument | LibraryDocument;

/** Inputs for {@link FluxAgentDocumentEditor}. */
export interface FluxAgentDocumentEditorProps<D extends EditableDocument> {
  /** Server copy being edited; changing its path, version or revision resets the draft. */
  document: D;
  /** Accessible label for the content field, e.g. "System prompt". */
  label: string;
  /**
   * Persist `content` as the next version. The caller supplies the concurrency token for the
   * document it passed in (`expectedVersion` for memory, `expectedRevision` for library files).
   * Restoring a revision calls this too, with the old content. Resolves with the stored document.
   */
  onSave: (
    content: string,
    changeNote: string
  ) => Promise<
    FluxAgentResult<{
      /** Stored document after the save, carrying the new version or revision. */
      document: D;
    }>
  >;
  /** Called after a successful save with the new server copy. */
  onSaved: (document: D) => void;
  /** Re-read the latest server copy after a version conflict. */
  onReload: () => Promise<void> | void;
  /** Optional delete; the caller confirms first. Omit to hide the button. */
  onDelete?: () => void;
  /** Show a change-note field (trainer documents). Defaults to false. */
  withChangeNote?: boolean;
  /** Optional commit history loader; enables the history list. */
  loadHistory?: () => Promise<
    FluxAgentResult<{
      /** Revisions newest first, as returned by the history route. */
      revisions: LibraryRevision[];
    }>
  >;
  /** Short helper text under the title. */
  description?: string;
}

/** Format a server timestamp as `MMM d, yyyy h:mm a`; unparsable input is returned unchanged. */
function formatWhen(iso: string): string {
  const date = parseServerTime(iso);
  return Number.isNaN(date.getTime()) ? iso : format(date, 'MMM d, yyyy h:mm a');
}

/** True when the document is a trainer library file identified by a Git revision. */
function isLibraryDocument(document: EditableDocument): document is LibraryDocument {
  return 'revision' in document;
}

/** Concurrency token of the server copy; a change resets the editor draft. */
function baselineOf(document: EditableDocument): string {
  return isLibraryDocument(document) ? (document.revision ?? '') : String(document.version);
}

/** One-line description of the server copy shown under the path. */
function describe(document: EditableDocument): string {
  if (isLibraryDocument(document)) {
    return document.revision ? `Revision ${shortId(document.revision)}` : 'Not saved yet';
  }
  return (
    `Version ${document.version}` +
    (document.updatedAt ? ` · updated ${formatWhen(document.updatedAt)}` : '') +
    (document.updatedBy ? ` by ${document.updatedBy}` : '')
  );
}

/** Confirmation copy after a successful save. */
function savedText(document: EditableDocument): string {
  if (isLibraryDocument(document)) {
    return document.revision ? `Saved revision ${shortId(document.revision)}.` : 'Saved.';
  }
  return `Saved version ${document.version}.`;
}

/** Human label for a commit source. */
const SOURCE_LABELS: Record<LibraryRevision['source'], string> = {
  agent: 'agent',
  editor: 'console',
  import: 'import',
  seed: 'seed',
  coach: 'coaching',
  git: 'git push',
};

/** Edit one Markdown document with optimistic concurrency, optional change notes and history. */
export function FluxAgentDocumentEditor<D extends EditableDocument>({
  document,
  label,
  onSave,
  onSaved,
  onReload,
  onDelete,
  withChangeNote = false,
  loadHistory,
  description,
}: FluxAgentDocumentEditorProps<D>) {
  const [draft, setDraft] = useState(document.content);
  const [changeNote, setChangeNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'error' | 'ok'; text: string } | null>(null);
  const [conflict, setConflict] = useState(false);
  const [history, setHistory] = useState<LibraryRevision[] | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [viewing, setViewing] = useState<LibraryRevision | null>(null);
  const baseline = baselineOf(document);

  useEffect(() => setMessage(null), [document.path]);

  // A new server copy (after save or reload) replaces the draft but keeps the save confirmation.
  useEffect(() => {
    setDraft(document.content);
    setChangeNote('');
    setConflict(false);
    setHistory(null);
    setHistoryOpen(false);
    setViewing(null);
  }, [document.path, baseline, document.content]);

  const dirty = draft !== document.content;
  const tooLong = draft.length > MAX_DOCUMENT_CHARS;

  /** Save `content` with `note`, then report success or keep the draft on failure. */
  const persist = async (content: string, note: string) => {
    setSaving(true);
    setMessage(null);
    const result = await onSave(content, note);
    setSaving(false);
    if (result.ok) {
      setMessage({ tone: 'ok', text: savedText(result.data.document) });
      onSaved(result.data.document);
      return;
    }
    if (isVersionConflict(result)) setConflict(true);
    setMessage({ tone: 'error', text: result.message });
  };

  const reload = async () => {
    if (dirty && !window.confirm('Discard your unsaved edits and load the latest version?')) return;
    setConflict(false);
    setMessage(null);
    await onReload();
  };

  const toggleHistory = async () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (!next || history || !loadHistory) return;
    setHistoryLoading(true);
    const result = await loadHistory();
    setHistoryLoading(false);
    if (result.ok) setHistory(result.data.revisions);
    else setMessage({ tone: 'error', text: result.message });
  };

  const restore = async (revision: LibraryRevision) => {
    const id = shortId(revision.commit);
    const prompt = dirty
      ? `Discard your unsaved edits and restore commit ${id}?`
      : `Restore commit ${id}? This saves its content as a new commit.`;
    if (!window.confirm(prompt)) return;
    setViewing(null);
    await persist(revision.content, `Restore ${id}`);
  };

  return (
    <div className="flex min-h-0 flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-mono text-sm font-medium" title={document.path}>
            {document.path}
          </p>
          <p className="text-muted-foreground text-xs">{describe(document)}</p>
          {description && <p className="text-muted-foreground mt-1 text-xs">{description}</p>}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {loadHistory && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={toggleHistory}
              aria-expanded={historyOpen}
              className="gap-1.5"
            >
              <History className="h-3.5 w-3.5" /> History
            </Button>
          )}
          {onDelete && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onDelete}
              className="text-destructive hover:text-destructive gap-1.5"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </Button>
          )}
        </div>
      </div>

      {historyOpen && (
        <div className="bg-muted/30 rounded-md border p-2">
          {historyLoading ? (
            <p className="text-muted-foreground flex items-center gap-2 p-2 text-xs">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading history…
            </p>
          ) : !history?.length ? (
            <p className="text-muted-foreground p-2 text-xs">No commits yet.</p>
          ) : (
            <ul
              className="native-scroll max-h-48 space-y-1 overflow-y-auto"
              aria-label="Revision history"
            >
              {history.map(revision => {
                const current = isLibraryDocument(document)
                  ? !revision.deleted && revision.content === document.content
                  : false;
                return (
                  <li
                    key={revision.commit}
                    className="hover:bg-background flex flex-wrap items-center justify-between gap-2 rounded px-2 py-1.5 text-xs"
                  >
                    <div className="min-w-0">
                      <code className="font-mono font-medium">{shortId(revision.commit)}</code>
                      {revision.deleted && (
                        <Badge variant="destructive" className="ml-1.5 px-1.5 py-0 text-[10px]">
                          Deleted
                        </Badge>
                      )}
                      <span className="text-muted-foreground">
                        {' '}
                        · {revision.updatedBy || 'unknown author'} ·{' '}
                        {formatWhen(revision.createdAt)} · {SOURCE_LABELS[revision.source]}
                      </span>
                      {revision.changeNote && (
                        <p className="text-muted-foreground truncate">“{revision.changeNote}”</p>
                      )}
                    </div>
                    {!revision.deleted && (
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() =>
                            setViewing(viewing?.commit === revision.commit ? null : revision)
                          }
                          aria-pressed={viewing?.commit === revision.commit}
                        >
                          View
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 gap-1 px-2 text-xs"
                          onClick={() => void restore(revision)}
                          disabled={saving || current}
                          title={current ? 'Matches the current content' : undefined}
                        >
                          <RotateCcw className="h-3 w-3" /> Restore
                        </Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {viewing && (
            <div className="mt-2">
              <p className="mb-1 text-xs font-medium">
                Commit {shortId(viewing.commit)} (read-only)
              </p>
              <pre className="native-scroll bg-background max-h-56 overflow-auto whitespace-pre-wrap rounded p-2 font-mono text-xs">
                {viewing.content}
              </pre>
            </div>
          )}
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-1.5">
        <Label htmlFor={`flux-doc-${document.path}`}>{label}</Label>
        <Textarea
          id={`flux-doc-${document.path}`}
          value={draft}
          onChange={event => setDraft(event.target.value)}
          spellCheck
          className={cn(
            'min-h-[16rem] flex-1 font-mono text-xs leading-relaxed',
            tooLong && 'border-destructive'
          )}
        />
        {tooLong && (
          <p className="text-destructive text-xs">
            {draft.length.toLocaleString()} characters; the limit is{' '}
            {MAX_DOCUMENT_CHARS.toLocaleString()}.
          </p>
        )}
      </div>

      {withChangeNote && (
        <div className="space-y-1.5">
          <Label htmlFor={`flux-note-${document.path}`}>Change note (optional)</Label>
          <Input
            id={`flux-note-${document.path}`}
            value={changeNote}
            onChange={event => setChangeNote(event.target.value)}
            placeholder="What changed and why"
            maxLength={500}
          />
        </div>
      )}

      {message && (
        <div
          role={message.tone === 'error' ? 'alert' : 'status'}
          className={cn(
            'flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-xs',
            message.tone === 'error'
              ? 'border-destructive/40 bg-destructive/5 text-destructive'
              : 'border-primary/30 bg-primary/5'
          )}
        >
          <span>{message.text}</span>
          {conflict && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={reload}
            >
              Reload latest version
            </Button>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => setDraft(document.content)}
          disabled={!dirty || saving}
        >
          Discard changes
        </Button>
        <Button
          type="button"
          onClick={() => void persist(draft, changeNote.trim())}
          disabled={!dirty || saving || tooLong}
          className="gap-1.5"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </Button>
      </div>
    </div>
  );
}
